import { routes } from '@libs/renegkit/routes';

/**
 * Checkout object type that defines the structure of data used to find a specific debt to be renegotiated
 * used for redirections between different parts of the application.
 *
 * @typedef {Object} CheckoutObj
 * @property {string} [systemSlug] - Optional identifier for the system (e.g., 'itau-rdhi')
 * @property {string} creditorSlug - Required identifier for the creditor
 * @property {string} [contract] - Optional debt contract identifier
 * @property {number} [debtId] - Optional identifier for the debt
 * @property {number} [agreementValue] - Optional value of the agreement
 * @property {boolean} [fallbackToNegative] - Used by the "trigger negativados" campaign. When the
 *   payload has no checkout target (no `creditorSlug`), `true` sends the user straight to the
 *   negative debts page (no checkout). When there IS a checkout target, `true` acts as the
 *   not-found fallback: the debt is attempted first, and only if it can't be found the user lands
 *   on the negative debts page. `false`/absent keeps the current behavior.
 *   @example
 *   // Direct redirect: { "fallbackToNegative": true }
 *   // Checkout link with fallback: { "creditorSlug": "loja-x", "fallbackToNegative": true }
 */
export type CheckoutObj = {
  creditorSlug: string;
  brand?: string;
  fallbackToNegative?: boolean;
  systemSlug?: string; // deprecated
  contract?: string; // deprecated
  debtId?: number; // deprecated
  agreementValue?: number; // deprecated
  sortField?: 'aging'; // deprecated
  sortType?: 'desc' | 'asc'; // deprecated
};

/**
 * Direct redirect for a bare fallback payload: `fallbackToNegative === true` with no checkout
 * target (`creditorSlug`). Returns the negative debts path, or `null` when there's a checkout
 * target (then the normal flow runs and `getNotFoundRedirectPath` handles the fallback).
 *
 * @param {CheckoutObj | null} checkoutObj - The decoded checkout object (or null when absent)
 * @returns {string | null} The negative debts path, or null when there's a checkout target
 */
export const getForcedRedirectPath = (checkoutObj: CheckoutObj | null): string | null =>
  checkoutObj?.fallbackToNegative === true && !checkoutObj.creditorSlug ? routes.negativeOffers.path : null;

/**
 * Not-found fallback destination for a checkout link. Strictly `=== true`: `fallbackToNegative: true`
 * sends the user to the negative debts page when the debt can't be found; otherwise `defaultPath`.
 *
 * @param {CheckoutObj | null} checkoutObj - The decoded checkout object (or null when absent)
 * @param {string} defaultPath - The destination when the negative fallback isn't requested
 * @returns {string} The path to navigate to on not-found
 */
export const getNotFoundRedirectPath = (checkoutObj: CheckoutObj | null, defaultPath: string): string =>
  checkoutObj?.fallbackToNegative === true ? routes.negativeOffers.path : defaultPath;

/**
 * Extracts and parses the checkout parameter from the URL's query string.
 * The parameter is expected to be base64 encoded and contain a JSON object.
 *
 * @returns {CheckoutObj | null} The decoded checkout object or null if parameter is missing or invalid
 *
 * @example
 * // For URL: https://example.com/?checkout=eyJjcmVkaXRvclNsdWciOiJteS1jcmVkaXRvciJ9
 * const config = getCheckoutRedirectConfigFromURL();
 * // Returns: { creditorSlug: "my-creditor" }
 */
export const getCheckoutRedirectConfigFromURL = (): CheckoutObj | null => {
  const paramName = 'checkout';
  const pageParams = new URLSearchParams(window.location.search);
  const redirectParam = pageParams.get(paramName);

  if (!redirectParam) return null;
  try {
    // Fix the issue where '+' is interpreted as whitespace by URLSearchParams
    // Replace whitespaces back to '+' and then URL decode if necessary
    // https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams#preserving_plus_signs
    const correctedParam = redirectParam.replace(/\s/g, '+');

    const urlEncodedBase64Characters = /%2B|%2F|%3D/;
    const shouldURLDecode = urlEncodedBase64Characters.test(correctedParam);
    const usedRedirectParam = shouldURLDecode ? decodeURIComponent(correctedParam) : correctedParam;

    const decodedParam = window.atob(usedRedirectParam);
    const JSONDecoded = JSON.parse(decodedParam);

    // Engage may serialize booleans as strings — normalize `fallbackToNegative` to a real boolean.
    // Only the string "true" means true; any other string is treated as `false` (safe default).
    if (typeof JSONDecoded.fallbackToNegative === 'string') {
      JSONDecoded.fallbackToNegative = JSONDecoded.fallbackToNegative === 'true';
    }

    return JSONDecoded as CheckoutObj;
  } catch (err) {
    console.error(err);
    return null;
  }
};

/**
 * Creates a base64 encoded parameter from a checkout object.
 * This encoded string can be used as a URL parameter for redirections.
 *
 * @param {CheckoutObj} checkoutObj - The checkout object to encode
 * @returns {string} The base64 encoded string representation of the checkout object
 *
 * @example
 * const param = createCheckoutRedirectParam({ creditorSlug: "my-creditor" });
 * // Returns: "eyJjcmVkaXRvclNsdWciOiJteS1jcmVkaXRvciJ9"
 */
export const createCheckoutRedirectParam = (checkoutObj: CheckoutObj) => {
  const jsonEncoded = JSON.stringify(checkoutObj);
  const base64Encoded = window.btoa(jsonEncoded);
  return base64Encoded;
};

/**
 * Generates a confirmation URL based on either a provided checkout configuration or the checkout parameter in the current URL.
 * Uses the creditor slug from the checkout parameter to create a path to the confirmation page.
 *
 * @param {CheckoutObj} [checkoutRedirectConfig] - Optional checkout configuration object. If provided, it will be used instead of reading from URL
 * @returns {string | null} The confirmation URL or null if checkout parameter is missing or invalid
 *
 * @example
 * // Reading from URL: https://example.com/?checkout=eyJjcmVkaXRvclNsdWciOiJteS1jcmVkaXRvciJ9
 * const url = getCheckoutRedirectUrl();
 * // Returns: "/my-creditor/confirmacao?checkout=eyJjcmVkaXRvclNsdWciOiJteS1jcmVkaXRvciJ9"
 *
 * @example
 * // Using provided configuration
 * const config = { creditorSlug: "my-creditor", debtId: 123 };
 * const url = getCheckoutRedirectUrl(config, true);
 * // Returns: "/checkout/oferta/my-creditor?offerId=123"
 */
export const getCheckoutRedirectUrl = (checkoutRedirectConfig?: CheckoutObj, isRenegkitVariant?: boolean) => {
  const checkoutParam = checkoutRedirectConfig ? checkoutRedirectConfig : getCheckoutRedirectConfigFromURL();
  if (!checkoutParam) return null;

  const forcedRedirectPath = getForcedRedirectPath(checkoutParam);
  if (forcedRedirectPath) return forcedRedirectPath;

  const searchParams = new URLSearchParams(window.location.search);
  if (checkoutRedirectConfig) {
    searchParams.set('checkout', createCheckoutRedirectParam(checkoutRedirectConfig));
  }

  if (searchParams.get('e')) {
    searchParams.delete('e');
  }

  if (isRenegkitVariant) {
    const newParams = new URLSearchParams();
    if (checkoutParam.systemSlug) newParams.set('systemSlug', checkoutParam.systemSlug);
    if (checkoutParam.debtId) newParams.set('offerId', String(checkoutParam.debtId));
    const query = newParams.toString();
    return `/checkout/oferta/${checkoutParam.creditorSlug}${query ? `?${query}` : ''}`;
  }

  return `/${checkoutParam.creditorSlug}/confirmacao?${searchParams.toString()}`;
};

export const cleanCheckoutRedirectParam = () => {
  const checkoutRedirectParams = getCheckoutRedirectConfigFromURL();
  if (!checkoutRedirectParams) return;

  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete('checkout');
  window.history.replaceState({}, '', `${window.location.pathname}?${searchParams.toString()}`);
};
